import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfoResponse} from '@di-shared/api/di/__generated';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryManageTagsProps} from '@di-shared/features/history/tags/HistoryManageTags/index';
import {TagFormBottomSheetResult} from '@di-shared/features/history/tags/TagFormBottomSheet/index';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export default function HistoryManageTagsKr({vin}: HistoryManageTagsProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

const MAX_CUSTOM_TAGS = 7;

function Inner({vin}: HistoryManageTagsProps) {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T20415')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSection key={vin} />
        </section>
      </main>
    </div>
  );
}

function HistoryManageTagsSection() {
  return (
    <ErrorBoundary fallback={<HistoryManageTagsSectionErrorFallback />}>
      <Suspense fallback={<HistoryManageTagsSectionLoading />}>
        <HistoryManageTagsSectionInner />
      </Suspense>
    </ErrorBoundary>
  );
}

function HistoryManageTagsSectionInner() {
  const {t} = useInferableTranslation();
  const {openConfirm} = useCcWebviewUtilsHelper();

  const {data: tags, refetch} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfoResponse>>({
    queryKey: [getQueryKey(diApi.tripTags.getAllTags)],
    queryFn: () => diApi.tripTags.getAllTags(),
    select: ({data}: ApiResponseTripTagListResponse) => data.tags,
  });

  const numCustomTags = tags.filter(({tagType}) => tagType === 'CUSTOM').length;
  const isCustomTagsLimitReached = numCustomTags >= MAX_CUSTOM_TAGS;

  const [deleteTag] = usePendingHandler(
    async (tagId: string) => {
      const isConfirm = await openConfirm({
        title: t('T20418'),
        message: t('T20419'),
        okText: t('T10154'),
        cancelText: t('T20119'),
      });

      if (!isConfirm) {
        return;
      }

      await diApi.tripTags.deleteTag({tagId});
      await refetch();
      WebviewUtils.showToast(t('T20432'));
    },
    async () => {
      WebviewUtils.showToast(t('T20433'));
    }
  );

  const [createTag] = usePendingHandler(
    async (tagName: string) => {
      await diApi.tripTags.createTag({
        requestBody: {
          tagName,
        },
      });
      WebviewUtils.showToast(t('T20428'));
      await refetch();
    },
    async () => {
      WebviewUtils.showToast(t('T20429'));
    }
  );

  const [editTag] = usePendingHandler(
    async ({tagId, nextTagName}: {tagId: string; nextTagName: string}) => {
      await diApi.tripTags.updateTag({
        tagId,
        requestBody: {
          tagName: nextTagName,
        },
      });
      WebviewUtils.showToast(t('T20430'));
      await refetch();
    },
    async () => {
      WebviewUtils.showToast(t('T20431'));
    }
  );

  async function openCreateTagBottomSheet() {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<TagFormBottomSheetResult | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAGS_FORM.getFullPath()
    );

    if (!result || typeof result === 'boolean' || !result.tagName) {
      return;
    }

    await createTag(result.tagName);
  }

  async function openEditTagBottomSheet(tagId: string, tagName: string) {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<TagFormBottomSheetResult | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAGS_FORM.getFullPath(),
      {
        initialTagName: tagName,
      }
    );

    if (!result || typeof result === 'boolean' || !result.tagName || result.tagName === tagName) {
      return;
    }

    await editTag({
      tagId,
      nextTagName: result.tagName,
    });
  }

  return (
    <>
      <div className="tag_managment">
        <ul className="tag_list">
          {tags.map(({tagId, tagName, tagType}) => (
            <li
              key={tagId}
              className="tag_item"
            >
              <span className="tag_name">{tagName}</span>
              {tagType !== 'BUSINESS' && (
                <div className="tag_actions">
                  <button
                    type="button"
                    className="btn_edit"
                    aria-label="Edit tag"
                    onClick={() => openEditTagBottomSheet(tagId, tagName)}
                  >
                    <span className="ic ic_edit_16px" />
                  </button>
                  <button
                    type="button"
                    className="btn_delete"
                    aria-label="Delete tag"
                    onClick={() => deleteTag(tagId)}
                  >
                    <span className="ic ic_delete_16px" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {isCustomTagsLimitReached ? (
          <p className="desc">
            <span className="ic ic_caution_18px" />
            {t('T20417')}
          </p>
        ) : (
          <button
            type="button"
            className="btn btn_round btn_round_outline btn_tag_add"
            onClick={() => openCreateTagBottomSheet()}
          >
            <span className="ic ic_add_12px" />
            {t('T20416')}
          </button>
        )}
      </div>
      <HowToTagGuide />
    </>
  );
}

function HistoryManageTagsSectionLoading() {
  return (
    <>
      <div
        className="tag_managment skeleton"
        style={{height: '240px'}}
      />
      <HowToTagGuide />
    </>
  );
}

function HistoryManageTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <div className="tag_managment">
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T20415')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSectionLoading />
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T20415')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSectionErrorFallback />
        </section>
      </main>
    </div>
  );
}

function HowToTagGuide() {
  const {t} = useInferableTranslation();
  const [isGuideOpen, setIsGuideOpen] = useState(true);
  return (
    <div className="section_container">
      <div className="driving_accordion type_container">
        <div className="driving_accordion_item">
          <h3>
            <button
              type="button"
              className="driving_accordion_btn"
              aria-expanded={isGuideOpen}
              aria-controls="accordion_content01"
              onClick={() => setIsGuideOpen((prev) => !prev)}
            >
              <span className="driving_accordion_title">{t('T20420')}</span>
              <span className="ic ic_arrow_down_16px" />
            </button>
          </h3>
          <div
            className="driving_accordion_content"
            id="accordion_content01"
            style={{display: isGuideOpen ? 'block' : 'none'}}
          >
            <ul className="tag_standard_list">
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_company_20px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('T20421')}</strong>
                  <span className="tag_standard_desc">{t('T20422')}</span>
                </div>
              </li>
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_person_24px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('T20423')}</strong>
                  <span className="tag_standard_desc">{t('T20424')}</span>
                </div>
              </li>
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_setting_20px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('T20425')}</strong>
                  <span className="tag_standard_desc">{t('T20426')}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
